import React, { useCallback, useEffect, useState } from "react";
import { Award, CheckCircle2, ClipboardCheck, LockKeyhole, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const AcademyExams = () => {
  const [exams,setExams] = useState([]);
  const [active,setActive] = useState(null);
  const [answers,setAnswers] = useState({});
  const [result,setResult] = useState(null);
  const [error,setError] = useState("");

  const load = useCallback(() => api.get("/api/academy/exams").then(({data}) => setExams(Array.isArray(data) ? data : [])).catch(e => setError(formatApiError(e))),[]);
  useEffect(() => { load(); }, [load]);

  const openExam = async (slug) => {
    setError(""); setResult(null); setAnswers({});
    try { const {data}=await api.get(`/api/academy/courses/${slug}/exam`); setActive(data); }
    catch(e){ setError(formatApiError(e)); }
  };

  const submit = async () => {
    if (!active.questions.every((_,i)=>answers[i] !== undefined)) { setError("Responde todas las preguntas antes de enviar."); return; }
    try {
      const {data}=await api.post(`/api/academy/courses/${active.course_slug}/exam/submit`, { answers:active.questions.map((_,i)=>Number(answers[i])) });
      setResult(data); setActive(null); await load();
    } catch(e){ setError(formatApiError(e)); }
  };

  return (
    <div className="mx-auto max-w-[1300px] pb-12" data-testid="academy-exams-page">
      {!active && <section className="relative mb-6 overflow-hidden rounded-[30px] border border-[#d4ab5e]/20 bg-[#0d0b08] p-6 md:p-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_15%,rgba(212,171,94,.14),transparent_34%)]" />
        <div className="relative">
          <div className="flex items-center gap-2 font-mont text-[8px] uppercase tracking-[.3em] text-[#aa956e]"><ShieldCheck size={12}/> Modo Concentración</div>
          <h1 className="mt-2 font-cinzel text-3xl text-[#f1dcae] md:text-4xl">Pruebas de dominio</h1>
          <p className="mt-3 max-w-3xl font-cormorant text-xl text-[#b3a895]">Las evaluaciones se habilitan cuando el sendero correspondiente está completo.</p>
        </div>
      </section>}

      {error && <div className="mb-5 rounded-2xl border border-red-400/20 bg-red-950/20 p-4 font-mont text-sm text-red-200">{error}</div>}

      {result && <div className={`mb-6 rounded-[24px] border p-6 ${result.passed ? "border-emerald-400/20 bg-emerald-950/10" : "border-amber-400/20 bg-amber-950/10"}`}>
        <div className="font-cinzel text-xl text-[#ead5aa]">{result.passed ? "Dominio acreditado" : "Revisión necesaria"}</div>
        <p className="mt-2 font-mont text-sm text-[#b6aa97]">Resultado: {result.score}% · Intentos restantes: {result.attempts_remaining}</p>
        {result.critical_failure && <p className="mt-2 font-cormorant text-red-200">Una respuesta crítica requiere revisión antes del siguiente intento.</p>}
        {result.certificate_code && <p className="mt-3 font-mont text-[10px] uppercase tracking-[.14em] text-[#d8bb7c]">Certificado: {result.certificate_code}</p>}
      </div>}

      {active ? (
        <section className="min-h-[70vh] rounded-[30px] border border-white/9 bg-[#0b0907] p-5 md:p-8">
          <div className="mb-7 border-b border-white/8 pb-5">
            <div className="font-mont text-[8px] uppercase tracking-[.28em] text-[#8e826f]">Evaluación · pregunta enfocada</div>
            <h2 className="mt-2 font-cinzel text-2xl text-[#efd9ac] md:text-3xl">{active.course_title}</h2>
            <p className="mt-2 font-mont text-[9px] text-[#827867]">Aprobación mínima {active.pass_percent}% · Intento {active.attempts_used + 1} de {active.max_attempts}</p>
          </div>
          <div className="space-y-5">
            {active.questions.map((q,qi) => <fieldset key={q.id} className="rounded-[22px] border border-white/9 bg-white/[.018] p-5">
              <legend className="px-2 font-cinzel text-base text-[#e5cfa4]">{qi + 1}. {q.prompt}{q.critical ? " · CRÍTICA" : ""}</legend>
              <div className="mt-4 space-y-2">{q.options.map((option,oi) => <label key={oi} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 font-mont text-sm transition ${answers[qi]===oi ? "border-[#d4ab5e]/35 bg-[#d4ab5e]/6 text-[#ead5aa]" : "border-white/8 text-[#aaa08d]"}`}>
                <input type="radio" name={q.id} checked={answers[qi]===oi} onChange={() => setAnswers({...answers,[qi]:oi})}/>{option}
              </label>)}</div>
            </fieldset>)}
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <button onClick={submit} className="rounded-xl bg-[#d4ab5e] px-6 py-3 font-mont text-[9px] font-bold uppercase tracking-[.14em] text-[#171008]">Enviar evaluación</button>
            <button onClick={() => setActive(null)} className="rounded-xl border border-white/10 px-6 py-3 font-mont text-[9px] uppercase tracking-[.14em] text-[#b7aa90]">Salir</button>
          </div>
        </section>
      ) : (
        <div className="grid gap-5 md:grid-cols-2">
          {exams.map((exam) => <article key={exam.course_slug} className="rounded-[24px] border border-white/9 bg-[#100d09] p-6">
            {exam.passed ? <Award size={28} className="text-emerald-400"/> : exam.lessons_completed ? <ClipboardCheck size={28} className="text-[#c5a463]"/> : <LockKeyhole size={28} className="text-[#756c5d]"/>}
            <h2 className="mt-3 font-cinzel text-xl text-[#ead5aa]">{exam.course_title}</h2>
            <p className="my-3 font-mont text-[9px] text-[#8e8371]">Mínimo {exam.pass_percent}% · {exam.attempts_used}/{exam.max_attempts} intentos</p>
            {exam.passed ? <div className="flex items-center gap-2 font-mont text-[9px] uppercase tracking-[.12em] text-emerald-300"><CheckCircle2 size={14}/> Aprobado</div> :
              <button disabled={!exam.lessons_completed || exam.attempts_used>=exam.max_attempts} onClick={() => openExam(exam.course_slug)} className="rounded-xl border border-[#d4ab5e]/30 px-4 py-2.5 font-mont text-[8px] uppercase tracking-[.13em] text-[#dec17f] disabled:opacity-40">{exam.lessons_completed ? "Presentar prueba" : "Completa el sendero"}</button>}
          </article>)}
          {!exams.length && <div className="rounded-[24px] border border-white/8 bg-white/[.02] p-10 font-cormorant text-xl text-[#a99d89]">Aún no tienes evaluaciones habilitadas.</div>}
        </div>
      )}
    </div>
  );
};
export default AcademyExams;
